// src/hooks/useFlowSenseLive.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — one way for every dashboard widget to listen to the live system
//
// Why: widgets were each subscribing to their own guessed event names
// ('work_order_status_updated', 'new_work_order_created' …) while the backend
// controllers actually broadcast 'work_order_events' { type, workOrderId },
// 'watchdog_events', 'road_update', 'anomaly_recorded', 'asset_prediction_update' …
// so most widgets never refreshed live. This hook:
//   • subscribes to ALL known names for a group (backend names + legacy aliases)
//   • also listens to the cross-page bus (utils/flowsenseBus) for changes made in
//     this browser (work order completed on the Work Orders page, asset reported …)
//   • coalesces bursts into ONE callback (debounce) and pauses while the tab is
//     hidden, catching up once when it becomes visible again
//   • subscribes ONCE — the latest callback is read through a ref
//
//   useFlowSenseLive(['workOrders', 'sla'], () => refetch(), { debounceMs: 800 })
//   const { events, counts } = useFlowSenseEventFeed({ max: 30 })   // activity feed
//
// --- UPDATE (SLA AI): 'sla' group now includes the SLA AI events —
//   sla_ai_decision (escalated / reassigned / recommended), sla_ai_warning (at risk),
//   sla_grade_recorded (a finished job was graded) — and 'alerts' includes
//   critical_alert_escalated. Activity-feed labels added for each.
// --- END UPDATE
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import webSocketService from '../services/websocketService';
import { subscribe as subscribeBus, BUS_EVENTS } from '../utils/flowsenseBus';

/** Backend event names (+ legacy aliases) grouped by what they mean. Unknown names are harmless. */
export const LIVE_GROUPS = Object.freeze({
    workOrders: ['work_order_events', 'work_order_created', 'work_order_updated', 'work_order_completed', 'work_order_assigned', 'work_order_deleted', 'work_order_status_updated', 'new_work_order_created'],
    sla: ['watchdog_events', 'sla_breach_detected', 'sla_ai_decision', 'sla_ai_warning', 'sla_grade_recorded'], // --- UPDATE: SLA AI
    alerts: ['new_sensor_alert', 'critical_anomaly', 'critical_anomaly_detected', 'anomaly_recorded', 'sensor_status_update', 'critical_alert_escalated'], // --- UPDATE
    predictions: ['asset_prediction_update', 'predictive_risk_update', 'ai_flood_prediction'],
    issues: ['new_reported_issue', 'issue_status_updated', 'issue_reported', 'helpdesk'],
    assets: ['asset_updated', 'asset_update', 'asset_created', 'asset_deleted', 'asset_status_changed', 'asset_status_update', 'utility_status_update', 'utility_updated', 'maintenance_logged'],
    roads: ['road_update', 'assessment_recorded', 'anomaly_clustered', 'asphalt_telemetry'], // --- UPDATE: asphalt tickets
    readings: ['new_water_level_reading'],
    inventory: ['inventory_updated'],
    health: ['system_health_update'],
});

/** Which FlowSense module each group belongs to (used by the activity feed + AI). */
export const GROUP_MODULE = Object.freeze({
    workOrders: 'work_orders', sla: 'work_orders', alerts: 'live_readings', predictions: 'digital_twin',
    issues: 'helpdesk', assets: 'public_utilities', roads: 'road_works', readings: 'live_readings',
    inventory: 'inventory', health: 'dashboard',
});

/** Cross-page bus messages → group. */
const BUS_GROUP = {
    [BUS_EVENTS.WORK_ORDER_CHANGED]: 'workOrders',
    [BUS_EVENTS.ASSET_CHANGED]: 'assets',
    [BUS_EVENTS.ROAD_CHANGED]: 'roads',
};

const EVENT_GROUP = Object.entries(LIVE_GROUPS).reduce((acc, [g, names]) => {
    names.forEach((n) => { if (!acc[n]) acc[n] = g; });
    return acc;
}, {});

const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/** Expands ['workOrders', 'my_custom_event'] → unique list of event names. */
export const resolveLiveEvents = (groupsOrEvents = []) => {
    const out = new Set();
    (Array.isArray(groupsOrEvents) ? groupsOrEvents : [groupsOrEvents]).forEach((g) => {
        if (LIVE_GROUPS[g]) LIVE_GROUPS[g].forEach(n => out.add(n));
        else if (g) out.add(g);
    });
    return [...out];
};

/** Short human label for a live event (activity feed). */
export const describeLiveEvent = (name, data = {}) => {
    const d = data && typeof data === 'object' ? (data.data && typeof data.data === 'object' ? data.data : data) : {};
    const id = d.workOrderId ?? d.work_order_id ?? d.id ?? d.asset_id ?? d.assetId ?? d.road_id ?? d.sensor_id ?? d.device_id ?? '';
    const short = id !== '' ? ` #${String(id).slice(0, 8)}` : '';
    switch (name) {
        case 'work_order_events': {
            const t = String(d.type || 'changed').replace(/_/g, ' ');
            return `Work order${short} ${t}${d.newStatus ? ` → ${String(d.newStatus).replace(/_/g, ' ')}` : ''}`;
        }
        case 'watchdog_events': return d.type === 'scan_complete' ? `SLA watchdog scan · efficiency ${d.efficiency_rating ?? '—'}%` : `SLA watchdog: ${String(d.type || 'update').replace(/_/g, ' ')}`;
        case 'road_update': return `Road / asset maintenance logged${short}`;
        case 'anomaly_recorded': case 'critical_anomaly': case 'critical_anomaly_detected': return `Sensor anomaly${short}`;
        case 'new_sensor_alert': return `New sensor alert${short}`;
        case 'asset_prediction_update': case 'predictive_risk_update': return `AI failure prediction updated${short}`;
        case 'ai_flood_prediction': return `AI flood prediction${d.wellName || d.well_id ? ` · ${d.wellName || d.well_id}` : ''}`;
        case 'new_water_level_reading': return `Water level ${d.level || 'reading'}${d.device_id ? ` · ${d.device_id}` : ''}`;
        case 'inventory_updated': return 'Inventory changed';
        // --- UPDATE: SLA AI
        case 'sla_ai_decision': {
            const verb = { reassign: 'reassigned', escalate: 'escalated', recommend: 'recommends reassigning', approve: 'reassignment approved', revert: 'reassignment reverted' }[d.type] || 'acted on';
            return `SLA AI ${verb} work order${short}`;
        }
        case 'sla_ai_warning': return `SLA at risk — work order${short}`;
        case 'sla_grade_recorded': return `Work order${short} graded ${d.grade || ''}${d.assigneeName ? ` · ${d.assigneeName}` : ''}`.trim();
        case 'critical_alert_escalated': return `Critical alert escalated to supervisors${d.title ? ` · ${String(d.title).slice(0, 40)}` : ''}`;
        // --- END UPDATE
        case 'helpdesk': return `Helpdesk ${String(d.event || 'update').replace(/_/g, ' ')}`;
        default: return `${name.replace(/_/g, ' ')}${short}`;
    }
};

/**
 * @param {string[]|string} groupsOrEvents  group keys from LIVE_GROUPS and/or raw event names
 * @param {(info:{events:string[], last:{name:string,data:any}})=>void} onLive  called once per burst
 * @param {{debounceMs?:number, enabled?:boolean, includeBus?:boolean, filter?:(name,data)=>boolean}} [opts]
 */
export default function useFlowSenseLive(groupsOrEvents, onLive, opts = {}) {
    const { debounceMs = 800, enabled = true, includeBus = true, filter } = opts;
    const cbRef = useRef(onLive);
    cbRef.current = onLive;
    const filterRef = useRef(filter);
    filterRef.current = filter;
    const key = resolveLiveEvents(groupsOrEvents).sort().join('|');

    useEffect(() => {
        if (!enabled) return undefined;
        const names = key ? key.split('|') : [];
        const groups = new Set(names.map(n => EVENT_GROUP[n]).filter(Boolean));
        let timer = null;
        let pendingHidden = false;
        let burst = [];
        let last = null;

        const flush = () => {
            timer = null;
            const info = { events: [...new Set(burst)], last };
            burst = [];
            try { cbRef.current?.(info); } catch (e) { /* a widget error must not break the socket */ }
        };
        const schedule = (name, data) => {
            if (filterRef.current && !filterRef.current(name, data)) return;
            burst.push(name);
            last = { name, data };
            if (isHidden()) { pendingHidden = true; return; }
            if (timer) return;
            timer = setTimeout(flush, debounceMs);
        };

        const handlers = names.map((name) => {
            const h = (data) => schedule(name, data);
            webSocketService?.subscribe?.(name, h);
            return [name, h];
        });

        const offBus = includeBus ? subscribeBus((type, payload) => {
            const g = BUS_GROUP[type];
            if (g && groups.has(g)) schedule(`bus:${type}`, payload);
        }) : () => {};

        const onVis = () => {
            if (!isHidden() && pendingHidden) {
                pendingHidden = false;
                if (!timer) timer = setTimeout(flush, Math.min(debounceMs, 300));
            }
        };
        document.addEventListener('visibilitychange', onVis);

        return () => {
            handlers.forEach(([n, h]) => webSocketService?.unsubscribe?.(n, h));
            offBus();
            document.removeEventListener('visibilitychange', onVis);
            clearTimeout(timer);
        };
    }, [key, debounceMs, enabled, includeBus]);
}

/**
 * Live activity feed across the whole system (for the AI command center).
 * State is flushed at most once per `flushMs` so a noisy socket cannot re-render the page per event.
 * @returns {{ events: Array<{id,name,group,module,label,at,data}>, counts: Record<string,number>, total:number, clear:()=>void }}
 */
export function useFlowSenseEventFeed({ max = 30, flushMs = 1000, groups = Object.keys(LIVE_GROUPS) } = {}) {
    const [state, setState] = useState({ events: [], counts: {}, total: 0 });
    const queueRef = useRef([]);
    const mountedRef = useRef(true);
    const key = groups.join('|');

    useEffect(() => {
        mountedRef.current = true;
        const names = resolveLiveEvents(key.split('|'));
        let timer = null;
        const flush = () => {
            timer = null;
            if (!mountedRef.current || !queueRef.current.length) return;
            const batch = queueRef.current;
            queueRef.current = [];
            setState((prev) => {
                const counts = { ...prev.counts };
                batch.forEach(e => { counts[e.group] = (counts[e.group] || 0) + 1; });
                return { events: [...batch.reverse(), ...prev.events].slice(0, max), counts, total: prev.total + batch.length };
            });
        };
        const push = (name, data) => {
            const group = EVENT_GROUP[name] || 'other';
            queueRef.current.push({
                id: `${name}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                name, group, module: GROUP_MODULE[group] || 'dashboard',
                label: describeLiveEvent(name, data), at: new Date().toISOString(), data,
            });
            if (!timer) timer = setTimeout(flush, flushMs);
        };
        const handlers = names.map((name) => {
            const h = (data) => push(name, data);
            webSocketService?.subscribe?.(name, h);
            return [name, h];
        });
        const offBus = subscribeBus((type, payload = {}) => {
            const g = BUS_GROUP[type];
            if (!g) return;
            const label = `${type === BUS_EVENTS.WORK_ORDER_CHANGED ? 'Work order' : type === BUS_EVENTS.ROAD_CHANGED ? 'Road' : 'Asset'} ${payload.action || 'changed'} (this browser)`;
            queueRef.current.push({ id: `bus-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, name: type, group: g, module: GROUP_MODULE[g], label, at: new Date().toISOString(), data: payload });
            if (!timer) timer = setTimeout(flush, flushMs);
        });
        return () => {
            mountedRef.current = false;
            handlers.forEach(([n, h]) => webSocketService?.unsubscribe?.(n, h));
            offBus();
            clearTimeout(timer);
        };
    }, [key, max, flushMs]);

    const clear = useCallback(() => setState({ events: [], counts: {}, total: 0 }), []);
    return useMemo(() => ({ ...state, clear }), [state, clear]);
}
